import { observer } from 'mobx-react';
import { OrganisationSelectItem } from 'pages/private/components/organisation-select';
import React from 'react';
import css from './raptor-components.css';
import {
    BasicFormWithAlerts,
    IconTextButton,
    SelectFormItem,
    Spinner,
    TextAreaFieldItem,
    TextFieldItem,
    notification,
} from 'ui-lib';
import { useAuthStore, useRaptorComponentsStore } from 'store';
import { useAlertMessages } from 'pages/private/components/alert/use-messages';
import { useLoadingStates } from 'hooks/use-loading.hook';
import { inputAlphanumericRule } from 'pages/private/components/form-utils';
import { RaptorComponentHelpers } from './helpers';
import { useRaptorComponentNavigator } from '../hooks/use-raptor-component-navigator';
import componentAccountSelector from '../hooks/use-account-selector';
export const CreateComponentForm = observer(() => {
    const authStore = useAuthStore();
    const navigation = useRaptorComponentNavigator();
    const componentsStore = useRaptorComponentsStore();
    const alertMessages = useAlertMessages();
    const loadingHook = useLoadingStates();
    const componentTypes = React.useMemo(
        () => RaptorComponentHelpers.getComponentTypeOptions(authStore.isDPUAdmin),
        [authStore.isDPUAdmin],
    );
    const accountSelector = componentAccountSelector();

    const onSave = async (values: any) => {
        const result = await loadingHook.withSaving(async () => {
            if (!accountSelector.selectedAccount) {
                return notification.error({
                    message: 'Please select an account',
                });
            }
            return componentsStore.createComponent(
                accountSelector.selectedAccount.id,
                accountSelector.selectedAccount.uuid,
                RaptorComponentHelpers.toCreateComponentRequest(values),
            );
        });
        if (result.isSuccess()) {
            notification.success({
                message: `Component ${values.name} created successfully`,
            });
            return navigation.toComponentListingPage({ accountUuid: accountSelector.selectedAccount?.uuid });
        }
        return notification.error({
            message: `Failed to create component ${values.name}`,
            description: result.errorMessage,
        });
    };

    return (
        <React.Fragment>
            <Spinner spinning={loadingHook.saving} className={css.spinner}>
                <BasicFormWithAlerts
                    onSubmit={onSave}
                    cardPros={{
                        title: 'Create a new Component',
                        size: 'default',
                    }}
                    errorMessages={alertMessages.errorMessages}
                    warningMessages={alertMessages.warningMessages}
                    items={[
                        <OrganisationSelectItem
                            code='account'
                            label='Account'
                            colon={true}
                            labelAlign='right'
                            initialValue={accountSelector.selectedAccount}
                            hidden={!accountSelector.isDPUAdmin}
                            isRequired={true}
                            selectProps={{
                                treeDataSource: accountSelector.dataSource,
                                expandAll: true,
                                value: accountSelector.selectedAccount?.id,
                                onChange: accountSelector.onChange,
                            }}
                        />,
                        <SelectFormItem
                            code='type'
                            label='Type'
                            labelAlign='right'
                            isRequired={true}
                            colon={true}
                            dataSource={componentTypes}
                            selectedValue={componentTypes[0]?.value}
                        />,
                        <TextFieldItem
                            label='Name'
                            isRequired={true}
                            code='name'
                            labelAlign='right'
                            colon={true}
                            rule={inputAlphanumericRule(128, ['MINUS', 'DOT'])}
                        />,
                        <TextAreaFieldItem
                            label='Description'
                            code='description'
                            labelAlign='right'
                            colon={true}
                            textAreaProps={{
                                autoSize: { minRows: 4, maxRows: 6 },
                            }}
                        />,
                        <div className={css['bottom-save-div']}>
                            <IconTextButton
                                label='Cancel'
                                type='default'
                                onClick={() =>
                                    navigation.toComponentListingPage({
                                        accountUuid: accountSelector.selectedAccount?.uuid,
                                    })
                                }
                            />
                            <IconTextButton label='Create' type='primary' htmlType='submit' />
                        </div>,
                    ]}
                />
            </Spinner>
        </React.Fragment>
    );
});
